<template>
  <div
    class="uni-uploader"
    :class="{ 'uni-uploader--multiple': multiple }"
  >
    <h4
      v-if="title"
      class="uni-uploader__title"
    >
      {{ title }}
    </h4>

    <!-- one file, already chosen: the file itself is the drop target (a new file replaces it) -->
    <div
      v-if="single"
      class="uni-uploader__single"
      :class="{ 'is-dragover': dragOver, 'is-error': single.status === 'error' }"
      @dragenter.prevent="onDragEnter"
      @dragover.prevent
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
    >
      <a
        v-if="single.isImage && single.url"
        class="uni-uploader__thumb"
        :href="single.status === 'done' ? single.url : null"
        target="_blank"
        rel="noopener"
      >
        <img
          :src="single.url"
          :alt="single.name"
        >
      </a>
      <span
        v-else
        class="uni-uploader__thumb uni-uploader__thumb--icon"
      >
        <feather-icon
          icon="FileTextIcon"
          size="28"
        />
        <small v-if="single.extension">{{ single.extension }}</small>
      </span>

      <div class="uni-uploader__info">
        <p
          class="uni-uploader__name"
          :title="single.name"
        >
          {{ single.name }}
        </p>
        <p
          v-if="single.status === 'uploading'"
          class="uni-uploader__state"
        >
          Загрузка… {{ single.progress }}%
        </p>
        <p
          v-else-if="single.status === 'error'"
          class="uni-uploader__state text-danger"
        >
          {{ single.error }}
        </p>
        <p
          v-else-if="single.sizeText"
          class="uni-uploader__state"
        >
          {{ single.sizeText }}
        </p>
        <b-progress
          v-if="single.status === 'uploading'"
          :value="single.progress"
          :max="100"
          height="4px"
          class="uni-uploader__progress"
        />
        <div class="uni-uploader__actions">
          <b-button
            v-if="single.status === 'error'"
            size="sm"
            variant="outline-danger"
            @click="retry(single)"
          >
            Повторить
          </b-button>
          <b-button
            v-if="single.status === 'done' && single.url"
            size="sm"
            variant="outline-primary"
            :href="single.url"
            target="_blank"
            rel="noopener"
          >
            Открыть
          </b-button>
          <b-button
            v-if="single.status !== 'uploading'"
            size="sm"
            variant="outline-primary"
            @click="openDialog"
          >
            Заменить
          </b-button>
          <b-button
            v-if="library && single.status !== 'uploading'"
            size="sm"
            variant="outline-primary"
            @click="libraryOpen = true"
          >
            Выбрать из файлов
          </b-button>
          <slot
            name="actions"
            :has-file="true"
          />
          <b-button
            v-if="removable && single.status !== 'uploading'"
            size="sm"
            variant="flat-danger"
            @click="remove(single)"
          >
            Убрать
          </b-button>
        </div>
      </div>
    </div>

    <!-- the drop zone: nothing chosen yet, or several files are allowed -->
    <div
      v-else
      class="uni-uploader__zone"
      :class="{ 'is-dragover': dragOver }"
      role="button"
      tabindex="0"
      :aria-label="zoneLabel"
      @click="openDialog"
      @keydown.enter.prevent="openDialog"
      @keydown.space.prevent="openDialog"
      @paste="onPaste"
      @dragenter.prevent="onDragEnter"
      @dragover.prevent
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
    >
      <feather-icon
        icon="UploadCloudIcon"
        size="28"
        class="uni-uploader__zone-icon"
      />
      <p class="uni-uploader__zone-text">
        {{ dragOver ? 'Отпустите, чтобы загрузить' : zoneLabel }}
      </p>
      <p
        v-if="hintText"
        class="uni-uploader__hint"
      >
        {{ hintText }}
      </p>
      <div
        v-if="library || $slots.actions || $scopedSlots.actions"
        class="uni-uploader__zone-actions"
        @click.stop
        @keydown.stop
      >
        <b-button
          v-if="library"
          size="sm"
          variant="outline-primary"
          @click="libraryOpen = true"
        >
          Выбрать из файлов
        </b-button>
        <slot
          name="actions"
          :has-file="false"
        />
      </div>
    </div>

    <input
      ref="input"
      type="file"
      class="uni-uploader__input"
      :accept="accept || null"
      :multiple="multiple"
      @change="onPick"
    >

    <file-library-modal
      v-if="libraryOpen"
      :multiple="multiple"
      :images-only="imagesOnly"
      @pick="pickFromLibrary"
      @close="libraryOpen = false"
    />

    <!-- several files: a tile per file -->
    <div
      v-if="multiple && items.length"
      class="uni-uploader__grid"
    >
      <div
        v-for="item in items"
        :key="item.key"
        class="uni-uploader__tile"
        :class="{
          'is-selected': isSelected(item),
          'is-error': item.status === 'error',
          'is-selectable': selectable && item.status === 'done',
        }"
        @click="toggleSelection(item)"
      >
        <slot
          name="file-preview"
          :file="item.file || item"
        >
          <img
            v-if="item.isImage && item.url"
            :src="item.url"
            :alt="item.name"
            class="uni-uploader__tile-img"
          >
          <span
            v-else
            class="uni-uploader__tile-icon"
          >
            <feather-icon
              icon="FileTextIcon"
              size="28"
            />
            <small>{{ item.extension || item.name }}</small>
          </span>
        </slot>

        <div
          v-if="item.status === 'uploading' || item.status === 'removing'"
          class="uni-uploader__tile-overlay"
        >
          <b-spinner small />
          <span v-if="item.status === 'uploading'">{{ item.progress }}%</span>
        </div>
        <div
          v-else-if="item.status === 'error'"
          class="uni-uploader__tile-overlay uni-uploader__tile-overlay--error"
          :title="item.error"
        >
          <span>{{ item.error }}</span>
          <b-button
            size="sm"
            variant="light"
            @click.stop="retry(item)"
          >
            Повторить
          </b-button>
        </div>

        <button
          v-if="removable && item.status !== 'uploading' && item.status !== 'removing'"
          type="button"
          class="uni-uploader__tile-remove"
          :aria-label="`Убрать ${item.name}`"
          @click.stop="remove(item)"
        >
          <feather-icon
            icon="XIcon"
            size="14"
          />
        </button>
        <a
          v-if="item.status === 'done' && item.url && !selectable"
          class="uni-uploader__tile-open"
          :href="item.url"
          target="_blank"
          rel="noopener"
          :aria-label="`Открыть ${item.name}`"
          @click.stop
        >
          <feather-icon
            icon="ExternalLinkIcon"
            size="14"
          />
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BProgress, BSpinner } from 'bootstrap-vue'
import FileLibraryModal from './FileLibraryModal.vue'
import {
  describeUploadError, fileExtension, fileUrl, formatBytes, isImageName, uploadFile, validateFile,
} from './upload'

let nextKey = 0

/**
 * The one uploader of the admin (FileUploader, FileMultiUploader and MultiFileUploader are thin adapters over it, each
 * keeping its old props and events). A drop zone — click, drag a file in, or paste a picture — that uploads at once,
 * shows the progress, and lets a failed file be retried. One file shows as a row with its preview and «Заменить» /
 * «Убрать»; several show as tiles.
 *
 * `files` is where the list starts (the files the record already has); after that the component owns the list and
 * reports it: `uploaded(file)`, `removed(file)`, `change(files)`, `queue(localFiles)`, `selection(files)`.
 * With `library` a file can also be chosen from the admin's file library («Выбрать из файлов») — it comes as
 * `uploaded(file)` too — and what is uploaded here goes to that library. The `actions` slot is the place for other ways
 * to choose a file (the photo archive).
 */
export default {
  name: 'UniUploader',
  components: {
    BButton, BProgress, BSpinner, FileLibraryModal,
  },
  props: {
    title: { type: String, default: '' },
    /** server file objects (`{ id, fullPath | path + name, name }`) the uploader starts with */
    files: { type: Array, default: () => [] },
    multiple: { type: Boolean, default: false },
    accept: { type: String, default: '' },
    /** e.g. ['.gpx'] — checked by the file's name before it is sent */
    allowedExtensions: { type: Array, default: () => [] },
    /** the API body limit is 50 MB */
    maxSizeMb: { type: Number, default: 50 },
    uploadEndpoint: { type: String, default: '/file/upload' },
    stringifyLastModified: { type: Boolean, default: false },
    hint: { type: String, default: '' },
    /** false: an uploaded file leaves the uploader — the page shows it in its own list */
    keepUploaded: { type: Boolean, default: true },
    removable: { type: Boolean, default: true },
    /**
     * true: «Убрать» also deletes the file on the server (`DELETE /file/:id`) — but never a file of the library:
     * another record may be showing it
     */
    deleteOnRemove: { type: Boolean, default: false },
    /** the admin's file library: files can be chosen from it, and the uploaded ones go to it */
    library: { type: Boolean, default: false },
    selectable: { type: Boolean, default: false },
    /** how many files are sent at the same time */
    concurrency: { type: Number, default: 3 },
  },
  data() {
    return {
      items: [],
      selected: [],
      dragOver: false,
      dragDepth: 0,
      active: 0,
      /** one-file mode: the file a new one is replacing, until the new one is on the server */
      replaced: null,
      libraryOpen: false,
    }
  },
  computed: {
    single() {
      return !this.multiple && this.items.length ? this.items[this.items.length - 1] : null
    },
    imagesOnly() {
      return /^image\//.test(this.accept)
    },
    zoneLabel() {
      return this.multiple
        ? 'Перетащите файлы сюда или нажмите, чтобы выбрать'
        : 'Перетащите файл сюда или нажмите, чтобы выбрать'
    },
    hintText() {
      if (this.hint) return this.hint
      const types = this.allowedExtensions.length ? this.allowedExtensions.join(', ') : ''
      return [types, `до ${this.maxSizeMb} МБ`].filter(Boolean).join(' · ')
    },
  },
  watch: {
    files: {
      immediate: true,
      handler(files) {
        this.reset(files)
      },
    },
  },
  beforeDestroy() {
    this.items.forEach(item => this.revoke(item))
  },
  methods: {
    /** Start over from the given server files; whatever is on its way to the server stays */
    reset(files) {
      const ids = list => list.map(file => file.id).join(',')
      const incoming = (files || []).filter(Boolean)
      const shown = this.items.filter(item => item.status === 'done' && item.file).map(item => item.file)
      // the page echoing back what the uploader has just reported is not a new list
      if (this.items.length && ids(incoming) === ids(shown)) return
      const busy = this.items.filter(item => ['queued', 'uploading', 'error'].includes(item.status))
      this.items.filter(item => !busy.includes(item)).forEach(item => this.revoke(item))
      const known = incoming.map(file => this.fromServer(file))
      this.items = this.multiple ? [...known, ...busy] : [...known.slice(-1), ...busy].slice(-1)
      this.selected = []
    },
    fromServer(file) {
      const url = fileUrl(file)
      const name = file.original_name || file.name || ''
      nextKey += 1
      return {
        key: `s${file.id || ''}-${nextKey}`,
        id: file.id,
        file,
        name,
        url,
        extension: fileExtension(name || url),
        isImage: isImageName(name) || isImageName(url),
        sizeText: formatBytes(file.size),
        status: 'done',
        progress: 100,
        error: '',
        local: null,
        objectUrl: null,
      }
    },
    fromLocal(local) {
      const isImage = (local.type || '').startsWith('image/') || isImageName(local.name)
      const objectUrl = isImage ? URL.createObjectURL(local) : null
      nextKey += 1
      return {
        key: `l${nextKey}`,
        id: null,
        file: null,
        name: local.name,
        url: objectUrl,
        extension: fileExtension(local.name),
        isImage,
        sizeText: formatBytes(local.size),
        status: 'queued',
        progress: 0,
        error: '',
        local,
        objectUrl,
      }
    },
    revoke(item) {
      if (item.objectUrl) URL.revokeObjectURL(item.objectUrl)
    },

    openDialog() {
      this.$refs.input.click()
    },
    onPick(event) {
      this.add(Array.from(event.target.files || []))
      // the same file can be picked again after a failure
      event.target.value = ''
    },
    onDragEnter() {
      this.dragDepth += 1
      this.dragOver = true
    },
    onDragLeave() {
      this.dragDepth = Math.max(0, this.dragDepth - 1)
      if (this.dragDepth === 0) this.dragOver = false
    },
    onDrop(event) {
      this.dragDepth = 0
      this.dragOver = false
      this.add(Array.from((event.dataTransfer && event.dataTransfer.files) || []))
    },
    onPaste(event) {
      const files = Array.from((event.clipboardData && event.clipboardData.files) || [])
      if (files.length) {
        event.preventDefault()
        this.add(files)
      }
    },

    add(locals) {
      const picked = this.multiple ? locals : locals.slice(0, 1)
      if (!picked.length) return
      this.$emit('queue', picked)

      picked.forEach(local => {
        const item = this.fromLocal(local)
        const problem = validateFile(local, { allowedExtensions: this.allowedExtensions, maxSizeMb: this.maxSizeMb })
        if (problem) {
          item.status = 'error'
          item.error = problem
          item.invalid = true
        }
        if (this.multiple) {
          this.items.push(item)
        } else {
          // the file being replaced stays aside until the new one is on the server
          this.replaced = this.items.find(existing => existing.status === 'done') || this.replaced || null
          this.items.forEach(existing => this.revoke(existing))
          this.items = [item]
        }
      })
      this.pump()
    },
    pump() {
      while (this.active < this.concurrency) {
        const item = this.items.find(candidate => candidate.status === 'queued')
        if (!item) return
        this.send(item)
      }
    },
    async send(item) {
      this.active += 1
      item.status = 'uploading'
      item.progress = 0
      item.error = ''
      try {
        const file = await uploadFile(item.local, {
          endpoint: this.uploadEndpoint,
          stringifyLastModified: this.stringifyLastModified,
          library: this.library,
          onProgress: percent => {
            item.progress = percent
          },
        })
        this.onUploaded(item, file)
      } catch (error) {
        item.status = 'error'
        item.error = describeUploadError(error)
        this.$emit('failed', { name: item.name, message: item.error })
      } finally {
        this.active -= 1
        this.pump()
      }
    },
    onUploaded(item, file) {
      const done = this.fromServer(file)
      // keep showing the local picture: no flicker while the uploaded one loads
      if (item.isImage && item.objectUrl) {
        done.objectUrl = item.objectUrl
        done.url = done.url || item.objectUrl
      }
      // the server names the file by a uuid; the person knows it by the name it had
      done.name = item.name || done.name
      done.sizeText = item.sizeText
      const index = this.items.indexOf(item)
      if (index === -1) return
      if (this.keepUploaded) this.items.splice(index, 1, done)
      else this.items.splice(index, 1)
      this.replaced = null
      this.$emit('uploaded', file)
      this.emitChange()
    },
    retry(item) {
      if (item.invalid || !item.local) {
        this.dismiss(item)
        this.openDialog()
        return
      }
      // eslint-disable-next-line no-param-reassign
      item.status = 'queued'
      this.pump()
    },
    /** Drop a file that never reached the server; in single mode the file it was replacing comes back */
    dismiss(item) {
      this.revoke(item)
      this.items = this.items.filter(existing => existing !== item)
      if (!this.multiple && this.replaced) {
        this.items = [this.replaced]
        this.replaced = null
      }
    },
    async remove(item) {
      if (item.status !== 'done') {
        this.dismiss(item)
        return
      }
      if (this.deleteOnRemove && item.id && !(item.file && item.file.in_library)) {
        item.status = 'removing'
        try {
          await this.$http.delete(`/file/${item.id}`)
        } catch (error) {
          item.status = 'done'
          this.$emit('failed', { name: item.name, message: `Не удалось удалить файл${describeUploadError(error, '')}` })
          return
        }
      }
      this.revoke(item)
      this.items = this.items.filter(existing => existing !== item)
      this.selected = this.selected.filter(existing => existing !== item)
      this.$emit('removed', item.file)
      this.emitChange()
    },
    /** Files chosen in the library: they are on the server already, so they arrive as uploaded ones */
    pickFromLibrary(files) {
      this.libraryOpen = false
      const fresh = files.filter(file => !this.items.some(item => item.id === file.id))
      if (!fresh.length) return
      fresh.forEach(file => {
        const item = this.fromServer(file)
        if (this.multiple) {
          this.items.push(item)
        } else {
          this.items.forEach(existing => this.revoke(existing))
          this.items = [item]
          this.replaced = null
        }
        if (!this.keepUploaded) this.items = this.items.filter(existing => existing !== item)
        this.$emit('uploaded', file)
      })
      this.emitChange()
    },
    emitChange() {
      this.$emit('change', this.items.filter(item => item.status === 'done' && item.file).map(item => item.file))
    },

    isSelected(item) {
      return this.selected.includes(item)
    },
    toggleSelection(item) {
      if (!this.selectable || item.status !== 'done') return
      this.selected = this.isSelected(item) ? this.selected.filter(existing => existing !== item) : [...this.selected, item]
      this.$emit('selection', this.selected.map(existing => existing.file))
    },
  },
}
</script>

<style lang="scss" scoped>
$line: #d8d6de;
$primary: #7367f0;
$danger: #ea5455;

.uni-uploader__title {
  margin-bottom: 0.5rem;
  font-size: 1rem;
  font-weight: 500;
}
.uni-uploader__input {
  display: none;
}

.uni-uploader__zone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 116px;
  padding: 16px;
  border: 2px dashed $line;
  border-radius: 8px;
  color: #6e6b7b;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;

  &:hover,
  &:focus-visible,
  &.is-dragover {
    border-color: $primary;
    background: rgba($primary, 0.06);
    outline: none;
  }
}
.uni-uploader__zone-icon {
  color: $primary;
}
.uni-uploader__zone-text {
  margin: 4px 0 0;
  font-weight: 500;
}
.uni-uploader__hint {
  margin: 0;
  font-size: 0.8rem;
  opacity: 0.8;
}
.uni-uploader__zone-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-top: 8px;
}

.uni-uploader__single {
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 116px;
  padding: 12px;
  border: 1px solid $line;
  border-radius: 8px;
  transition: border-color 0.15s ease, background-color 0.15s ease;

  &.is-dragover {
    border: 2px dashed $primary;
    background: rgba($primary, 0.06);
  }
  &.is-error {
    border-color: $danger;
  }
}
.uni-uploader__thumb {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 92px;
  height: 92px;
  overflow: hidden;
  border-radius: 6px;
  background: rgba(#6e6b7b, 0.1);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}
.uni-uploader__thumb--icon {
  flex-direction: column;
  gap: 4px;
  color: #6e6b7b;

  small {
    text-transform: uppercase;
  }
}
.uni-uploader__info {
  flex: 1;
  min-width: 0;
}
.uni-uploader__name {
  margin: 0;
  overflow: hidden;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.uni-uploader__state {
  margin: 2px 0 0;
  font-size: 0.85rem;
}
.uni-uploader__progress {
  margin-top: 6px;
}
.uni-uploader__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.uni-uploader__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(132px, 1fr));
  gap: 10px;
  margin-top: 12px;
}
.uni-uploader__tile {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 1;
  overflow: hidden;
  border: 1px solid $line;
  border-radius: 8px;
  background: rgba(#6e6b7b, 0.08);

  &.is-selectable {
    cursor: pointer;
  }
  &.is-selected {
    border: 2px solid $primary;
  }
  &.is-error {
    border-color: $danger;
  }
}
.uni-uploader__tile-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.uni-uploader__tile-icon {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  padding: 8px;
  color: #6e6b7b;

  small {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
.uni-uploader__tile-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px;
  background: rgba(#fff, 0.78);
  color: #5e5873;
  font-size: 0.8rem;
  text-align: center;
}
.uni-uploader__tile-overlay--error {
  background: rgba($danger, 0.9);
  color: #fff;

  span {
    display: -webkit-box;
    overflow: hidden;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
  }
}
.uni-uploader__tile-remove,
.uni-uploader__tile-open {
  position: absolute;
  top: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba(#000, 0.55);
  color: #fff;
  cursor: pointer;

  &:hover {
    background: rgba(#000, 0.8);
    color: #fff;
  }
}
.uni-uploader__tile-remove {
  right: 6px;
}
.uni-uploader__tile-open {
  left: 6px;
}

.dark-layout {
  .uni-uploader__zone,
  .uni-uploader__single,
  .uni-uploader__tile {
    border-color: #404656;
    color: #b4b7bd;
  }
  .uni-uploader__tile-overlay:not(.uni-uploader__tile-overlay--error) {
    background: rgba(#283046, 0.8);
    color: #d0d2d6;
  }
}
</style>
